import { Add, Edit } from '@mui/icons-material';
import IconButton from 'components/IconButton';
import { ModuleGuard } from 'components/ModuleGuard';
import RemoveConfirmationDialog from 'components/RemoveConfirmationDialog';
import { ConditionalTooltipWrapper } from 'components/Tooltip';
import { useAtomValue, useSetAtom } from 'jotai';
import { ModuleKey } from 'lib/api/company/types';
import callApi from 'lib/callApi';
import { isAdminUserLoggedIn } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import moment from 'moment';
import React, { Fragment, useMemo, useState } from 'react';
import {
  Alert,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';

import { manualMetricDefinitions } from './config';
import CreateCustomSectionModal from './CreateCustomSectionModal';
import CustomizeDashboardModal from './CustomizeDashboardModal';
import { getKPIErrorMessage, orderKPISections } from './helper';
import KPIDashboardUnavailable from './KPIDashboardUnavailable';
import KPISectionIcon from './KPISectionIcon';
import ManualDataModal from './ManualDataModal';
import SectionCard from './SectionCard';
import { kpiDashboardDataAtom, kpiRefreshAtom } from './state';
import styles from './styles.module.css';
import { KPIDashboardVisibilityPreferences, KPISection } from './types';

const defaultVisibilityPreferences: KPIDashboardVisibilityPreferences = {
  hiddenSectionKeys: [],
  hiddenMetricKeys: [],
  sectionOrder: [],
};

const manualSectionKeys = new Set<string>(
  manualMetricDefinitions.map((metric) => metric.section),
);

const loadVisibilityPreferences = (
  storageKey: string,
): KPIDashboardVisibilityPreferences => {
  try {
    const storedPreferences = JSON.parse(
      localStorage.getItem(storageKey) || '{}',
    ) as Partial<KPIDashboardVisibilityPreferences>;
    return {
      hiddenSectionKeys: Array.isArray(storedPreferences.hiddenSectionKeys)
        ? storedPreferences.hiddenSectionKeys.filter(
            (key): key is string => typeof key === 'string',
          )
        : [],
      // Metric-level visibility is no longer supported; ignore old browser
      // preferences so every metric appears under its enabled KPI section.
      hiddenMetricKeys: [],
      sectionOrder: Array.isArray(storedPreferences.sectionOrder)
        ? storedPreferences.sectionOrder.filter(
            (key): key is string => typeof key === 'string',
          )
        : [],
    };
  } catch (_error) {
    return defaultVisibilityPreferences;
  }
};

const getVisibilityStorageKey = (companyId: number) => {
  let userKey = 'current-user';
  try {
    userKey = localStorage.getItem('loggedInUser') || userKey;
  } catch (_error) {
    // Browser privacy settings may deny storage access; tenant scoping remains
    // intact by falling back to a non-identifying user key.
  }
  return `kpi-dashboard-visibility:${userKey}:${companyId}`;
};

const KPIDashboardContent = () => {
  const dashboardData = useAtomValue(kpiDashboardDataAtom);
  const setRefresh = useSetAtom(kpiRefreshAtom);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [selectedManualSection, setSelectedManualSection] =
    useState<KPISection | null>(null);
  const [createSectionModalOpen, setCreateSectionModalOpen] = useState(false);
  const [editingCustomSection, setEditingCustomSection] =
    useState<KPISection | null>(null);
  const [deletingCustomSection, setDeletingCustomSection] =
    useState<KPISection | null>(null);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const [savedPreferences, setSavedPreferences] = useState<{
    storageKey: string;
    preferences: KPIDashboardVisibilityPreferences;
  } | null>(null);
  const isAdminUser = isAdminUserLoggedIn();
  const visibilityStorageKey = dashboardData
    ? getVisibilityStorageKey(dashboardData.company_id)
    : '';
  const storedPreferences = useMemo(
    () =>
      visibilityStorageKey
        ? loadVisibilityPreferences(visibilityStorageKey)
        : defaultVisibilityPreferences,
    [visibilityStorageKey],
  );

  const refreshDashboard = () => setRefresh(Math.random().toString());

  const deleteCustomKPI = async () => {
    if (!deletingCustomSection?.id || !dashboardData) return;
    try {
      await callApi(
        `kpi/custom-sections/${deletingCustomSection.id}/`,
        'DELETE',
        {},
        { company_id: dashboardData.company_id },
        null,
        'kpi/custom-sections/:kpi-custom-section-delete',
      );
      showSuccessToast('Custom KPI deleted successfully.');
      refreshDashboard();
      setDeletingCustomSection(null);
    } catch (error) {
      showFailureToast(
        getKPIErrorMessage(error, 'Unable to delete custom KPI.'),
      );
    }
  };

  if (!dashboardData) {
    return (
      <Alert color="warning" className="mt-3">
        KPI dashboard data is currently unavailable.
      </Alert>
    );
  }

  if (!dashboardData.kpi_enabled) {
    return <KPIDashboardUnavailable />;
  }

  const editableSections = dashboardData.sections.filter(
    (section) =>
      (dashboardData.custom_kpis_enabled !== false && section.is_custom) ||
      manualSectionKeys.has(section.key),
  );
  const customKpiLimit = dashboardData.max_custom_kpis || 5;
  const canCreateCustomKPI =
    dashboardData.custom_kpis_enabled !== false &&
    dashboardData.sections.filter((section) => section.is_custom).length <
      customKpiLimit;
  const visibilityPreferences =
    savedPreferences?.storageKey === visibilityStorageKey
      ? savedPreferences.preferences
      : storedPreferences;
  const visibleSections = orderKPISections(
    dashboardData.sections,
    visibilityPreferences.sectionOrder,
  ).filter(
    (section) => !visibilityPreferences.hiddenSectionKeys.includes(section.key),
  );
  return (
    <Fragment>
      <div className="container-fluid p-0">
        <div className={styles.dashboardBody}>
          <Card className={`border border-light ${styles.dashboardMainCard}`}>
            <nav
              className={`navbar navbar-expand-sm bg-white border-bottom border-bottom-light sticky-top px-0 ${styles.toolbar}`}
            >
              <div className="row w-100 align-items-center mx-0">
                <Col xs="12" md="8">
                  <h4 className={styles.dashboardTitle}>KPI Dashboard</h4>
                </Col>
                <Col
                  xs="12"
                  md="4"
                  className={`d-flex justify-content-md-end ${styles.toolbarActions}`}
                >
                  {isAdminUser && (
                    <>
                      <UncontrolledDropdown>
                        <ConditionalTooltipWrapper tooltip="Update KPI data">
                          <DropdownToggle
                            id="edit-kpi-dashboard"
                            className={styles.updateDropdownToggle}
                            caret
                          >
                            <Edit fontSize="small" />
                            Update KPI Data
                          </DropdownToggle>
                        </ConditionalTooltipWrapper>
                        <DropdownMenu
                          className={styles.updateDropdownMenu}
                          right
                        >
                          {editableSections.map((section) => (
                            <DropdownItem
                              className={styles.updateDropdownItem}
                              key={section.key}
                              onClick={() => {
                                setSelectedManualSection(section);
                                setManualModalOpen(true);
                              }}
                            >
                              <KPISectionIcon
                                compact
                                sectionKey={section.key}
                              />
                              <span className={styles.updateDropdownLabel}>
                                {section.label}
                              </span>
                            </DropdownItem>
                          ))}
                          {canCreateCustomKPI && (
                            <>
                              <DropdownItem divider />
                              <DropdownItem
                                className={styles.updateDropdownItem}
                                onClick={() => {
                                  setEditingCustomSection(null);
                                  setCreateSectionModalOpen(true);
                                }}
                              >
                                <Add fontSize="small" />
                                <span className={styles.updateDropdownLabel}>
                                  Create Custom KPI
                                </span>
                              </DropdownItem>
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </>
                  )}
                </Col>
              </div>
            </nav>

            <CardBody className={styles.dashboardMainCardBody}>
              <div className={styles.sectionGrid}>
                {visibleSections.map((section) => (
                  <SectionCard
                    key={section.key}
                    section={section}
                    onDeleteCustomKPI={
                      isAdminUser && dashboardData.custom_kpis_enabled !== false
                        ? setDeletingCustomSection
                        : undefined
                    }
                    onEditCustomKPI={
                      isAdminUser && dashboardData.custom_kpis_enabled !== false
                        ? (customSection) => {
                            setEditingCustomSection(customSection);
                            setCreateSectionModalOpen(true);
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
              <div className={styles.dashboardFooter}>
                <div className={styles.asOfText}>
                  Updated {moment(dashboardData.as_of).format('lll')}
                </div>
                <IconButton
                  id="edit-kpi-display"
                  iconClass="fa fa-gear"
                  tooltip="Display options"
                  onClick={() => setCustomizeModalOpen(true)}
                />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <CustomizeDashboardModal
        isOpen={customizeModalOpen}
        toggle={() => setCustomizeModalOpen((open) => !open)}
        sections={dashboardData.sections}
        preferences={visibilityPreferences}
        onSave={(preferences) => {
          try {
            localStorage.setItem(
              visibilityStorageKey,
              JSON.stringify(preferences),
            );
          } catch (_error) {
            // Storage can be blocked or full; keep the current preferences and
            // report the failure instead of leaving the UI in an unsaved state.
            showFailureToast('Unable to save KPI display options.');
            return;
          }
          setSavedPreferences({
            storageKey: visibilityStorageKey,
            preferences,
          });
        }}
      />

      {isAdminUser && (
        <>
          <ManualDataModal
            isOpen={manualModalOpen}
            toggle={() => setManualModalOpen((open) => !open)}
            section={selectedManualSection}
            onSaved={refreshDashboard}
          />
          <CreateCustomSectionModal
            isOpen={createSectionModalOpen}
            maxItems={dashboardData.custom_kpi_max_items || 7}
            section={editingCustomSection}
            toggle={() => setCreateSectionModalOpen((open) => !open)}
            onSaved={refreshDashboard}
          />
          <RemoveConfirmationDialog
            isOpen={Boolean(deletingCustomSection)}
            toggle={() => setDeletingCustomSection(null)}
            removeConfirmationTitle="Delete Custom KPI"
            removeConfirmationMessage={`Permanently delete "${deletingCustomSection?.label || ''}" and all of its historical values? This action cannot be undone.`}
            onRemove={deleteCustomKPI}
          />
        </>
      )}
    </Fragment>
  );
};

const KPIDashboard = () => (
  <ModuleGuard module={ModuleKey.KPI} fallback={<KPIDashboardUnavailable />}>
    {/* Mount data consumers only after module access is confirmed so disabled
        tenants do not start KPI dashboard or history requests. */}
    <KPIDashboardContent />
  </ModuleGuard>
);

export default KPIDashboard;
